import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import { fetchEnterpriseRbacCapabilities } from '../lib/enterprise-api';

export function useEnterpriseCapabilities() {
  const { accessToken } = useAuth();
  const { activeOrgId, hasActiveOrgAccess } = useEnterpriseOrg();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !activeOrgId || !hasActiveOrgAccess) {
      setRole(null);
      setCapabilities([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    void fetchEnterpriseRbacCapabilities(accessToken, activeOrgId)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setRole(null);
          setCapabilities([]);
          setError(res.error || 'Could not load capabilities');
          return;
        }
        setRole(typeof res.role === 'string' ? res.role : null);
        setCapabilities(Array.isArray(res.capabilities) ? res.capabilities : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setRole(null);
        setCapabilities([]);
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, activeOrgId, hasActiveOrgAccess]);

  const capabilitySet = useMemo(() => new Set(capabilities), [capabilities]);
  const hasCapability = (capability: string) => capabilitySet.has(capability);

  return {
    loading,
    error,
    role,
    capabilities,
    hasCapability,
  };
}
